import { Link } from 'react-router-dom'

import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { MODE_LABEL } from '@/components/features/planning/sidebar/supplier-orders-helpers'
import type { SupplierOverviewDto } from '@/lib/api/v2/planning'
import { addressSummary, inProgressOrdersUrl, isOwnBrand } from '@/lib/supplierDirectory'
import { cn } from '@/lib/utils'

function confirmationLabel(s: SupplierOverviewDto): string {
  if (!s.expectsConfirmation) return 'Ne confirme pas'
  return s.defaultConfirmationDelayDays ? `Sous ${s.defaultConfirmationDelayDays} jours` : 'Délai par défaut'
}

interface SupplierListTableProps {
  suppliers: SupplierOverviewDto[]
  onOpen: (supplierId: string) => void
}

/** « Par fournisseur » — one row per supplier, its brands first (01-page-fournisseurs.md § P2). */
export function SupplierListTable({ suppliers, onOpen }: SupplierListTableProps) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Fournisseur</TableHead>
            <TableHead>Marques</TableHead>
            <TableHead>Commandé par</TableHead>
            <TableHead>Confirmation</TableHead>
            <TableHead>Adresses</TableHead>
            <TableHead className="text-right">En cours</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {suppliers.map((s) => {
            const addresses = addressSummary(s.emailAddresses)
            return (
              <TableRow key={s.id} data-testid={`suppliers-row-${s.id}`}>
                <TableCell className="font-medium">
                  <button
                    type="button"
                    onClick={() => onOpen(s.id)}
                    className="text-left hover:underline focus-visible:underline focus-visible:outline-none"
                    data-testid="suppliers-row-open-btn"
                  >
                    {s.name}
                  </button>
                </TableCell>
                <TableCell>
                  {s.brands.length === 0 ? (
                    <span className="text-sm text-muted-foreground">Aucune</span>
                  ) : (
                    <div className="flex flex-wrap gap-1" data-testid="suppliers-row-brands">
                      {s.brands.map((b) => (
                        <Badge
                          key={b.id}
                          variant="outline"
                          className={cn('font-normal', isOwnBrand(s.name, b.name) && 'border-primary text-primary')}
                        >
                          {b.name}
                        </Badge>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-sm">{s.allowedModes.map((m) => MODE_LABEL[m]).join(' · ')}</TableCell>
                <TableCell className="text-sm">{confirmationLabel(s)}</TableCell>
                <TableCell className="text-sm">
                  {addresses.main ? (
                    <>
                      <span className="font-mono text-xs">{addresses.main}</span>
                      {addresses.more > 0 && (
                        <span className="ml-1 text-xs text-muted-foreground">
                          + {addresses.more} adresse{addresses.more > 1 ? 's' : ''}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-muted-foreground">Aucune</span>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {s.inProgressOrderCount > 0 ? (
                    <Link
                      to={inProgressOrdersUrl(s.id)}
                      className="font-medium text-primary hover:underline"
                      title={`Voir les commandes en cours de ${s.name}`}
                      data-testid="suppliers-row-in-progress-link"
                    >
                      {s.inProgressOrderCount}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">0</span>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
